import { Box, Checkbox, FormControlLabel, Grid, IconButton, Tooltip as MuiTooltip, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAxios } from "../../utils/request";
import { CustomTooltip, getPlatformDisplayName, multiplyObjectsByKey, sumObjectItems, PLATFORMS } from "../../utils/utils";
import CheckboxList from "../CheckboxList";
import KoefPopover from "../KoefPopover";
import { useAppState } from "../../context/zustand";
import moment from "moment";
import { buildGraphCsvFilename, downloadGraphCsv, getCsvDateRange, getEpisodeCsvColumns } from "../../utils/graphCsv";
import { useEpisodeSelectionConsumer } from "../../hooks/useEpisodeSelectionConsumer";
import { EPISODE_MODE_NONE, EPISODE_MODE_SELECTED } from "../../utils/episodeSelection";






const useStyles = makeStyles(() => ({
    root: {
        paddingInline: 30
    }
}));



export default function EpisodeReleaseModul(props) {
    const theme = useTheme()
    const classes = useStyles();
    const podcast = useAppState(s => s.podcast?.name);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const intervalStartDate = useAppState(s => s.intervalStartDate);
    const intervalEndDate = useAppState(s => s.intervalEndDate);
    const collectedStartWeek = useAppState(s => s.collectedStartWeek);
    const collectedEndWeek = useAppState(s => s.collectedEndWeek);
    const episodeMode = useAppState(s => s.episodeMode);
    const selectedEpisodeIds = useAppState(s => s.selectedEpisodeIds);
    const [rawData, setRawData] = useState([]);
    const [episodes, setEpisodes] = useState([]);
    const [platformKoef, setPlatformKoef] = useState({})
    const [isBar, setIsBar] = useState(true);
    const [isLine, setIsLine] = useState(true);
    const selectedPodcast = useAppState(s => s.podcast)
    const selectedEpisodeIdSet = new Set(selectedEpisodeIds.map(String));
    const { revision, acknowledgeAfterPaint } = useEpisodeSelectionConsumer('episode-release');

    useEffect(() => {
        setPlatformKoef(props.coef)
    }, [props.coef])

    const episodesRequest = useAxios(
        ["episodes", podcast, intervalPreset, intervalStartDate, intervalEndDate],
        {
            method: 'get',
            url: `api/episodes/`,
            params:{
                ordering:'release_date',
                podcast__name: podcast
            }
        },
        {enabled: Boolean(podcast)},
        () => {},
        (err) => {}
    )

    useEffect(() => {
        if (episodesRequest.isError) {
            setRawData([]);
            return;
        }

        if (episodesRequest.data) {
            setRawData(episodesRequest.data.data.results);
        }
    }, [episodesRequest.data, episodesRequest.isError]);

    useEffect(() => {
        if (episodeMode === EPISODE_MODE_NONE) {
            setEpisodes([]);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (rawData) {
            let data = processData(rawData);
            setEpisodes(data)
            acknowledgeAfterPaint(revision);
        }
        // eslint-disable-next-line
    }, [platformKoef, intervalPreset, intervalStartDate, intervalEndDate, episodeMode, selectedEpisodeIds, revision, acknowledgeAfterPaint])

    const processData = (resData) => {

        return resData
            .filter(item => {
                if (intervalPreset === 'all_time') {
                    return true
                }

                const releaseDate = moment(item.release_date, 'YYYY-MM-DD');
                return releaseDate.isSameOrAfter(moment(intervalStartDate, 'YYYY-MM-DD')) &&
                    releaseDate.isSameOrBefore(moment(intervalEndDate, 'YYYY-MM-DD'));
            })
            .filter(item => episodeMode !== EPISODE_MODE_SELECTED || selectedEpisodeIdSet.has(String(item.id)))
            .filter(item => (sumObjectItems(item.release_week_quantity, {}) !== 0))
            .map(item => (
                {
                    id: item.id,
                    name: item.name,
                    ...multiplyObjectsByKey(item.release_week_quantity, platformKoef),
                    release_date: item.release_date,
                    total: sumObjectItems(item.release_week_quantity, platformKoef),
                }))

    }

    const handleDownloadCsv = () => {
        const range = getCsvDateRange({
            intervalPreset,
            intervalStartDate,
            intervalEndDate,
            collectedStartWeek,
            collectedEndWeek,
            rows: episodes,
            rowDateKey: 'release_date',
        });

        downloadGraphCsv({
            rows: episodes,
            columns: getEpisodeCsvColumns(platformKoef),
            filename: buildGraphCsvFilename({
                podcast: selectedPodcast?.name || podcast,
                startDate: range.startDate,
                endDate: range.endDate,
                graph: 'release',
            }),
        });
    }

    return (
        <>
            <Grid container spacing={3} className={classes.root}>
                <Grid item lg={12}>
                    <Box paddingY={5} display="flex" alignItems="center" justifyContent="space-between" flexDirection="row">

                        <Box display="flex" alignItems="center" justifyContent="space-between" flexDirection="row">
                            <Typography align="left" variant='h4'>{`First ${selectedPodcast?.release_week_length ?? ''} weeks after the release`}</Typography>
                        </Box>

                        <Box display="flex" alignItems="center" flexDirection="row">
                            <Box paddingRight={2}>
                                <KoefPopover values={platformKoef} handleChange={
                                    (value) => {
                                        setPlatformKoef(value)
                                    }
                                } >

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isBar}
                                                onChange={() => {
                                                    setIsBar(!isBar);
                                                }}
                                                color="primary"
                                            />
                                        }
                                        label="Platform bars"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isLine}
                                                onChange={() => {
                                                    setIsLine(!isLine);
                                                }}
                                                color="primary"
                                            />
                                        }
                                        label="Line"
                                    />

                                </KoefPopover>
                            </Box>
                            <CheckboxList
                                values={platformKoef}
                                defaultValues={props.coef}
                                handleChange={
                                    (value) => {
                                        setPlatformKoef(value);
                                    }
                                }
                            />
                            <MuiTooltip title="Download .csv">
                                <span>
                                    <IconButton
                                        aria-label="Download .csv"
                                        color="primary"
                                        disabled={episodes.length === 0}
                                        onClick={handleDownloadCsv}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </span>
                            </MuiTooltip>

                        </Box>

                    </Box>

                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                    {episodeMode === EPISODE_MODE_NONE ? (
                        <Box height={500} display="flex" alignItems="center" justifyContent="center">
                            <Typography color="textSecondary">Select at least one episode.</Typography>
                        </Box>
                    ) : <ResponsiveContainer width="100%" height={500}>
                        <ComposedChart
                            data={episodes}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 40,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="id" textAnchor="end" angle={-45} interval={parseInt(episodes.length / 20)} />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            {isBar && <Bar dataKey="blubrry" name={getPlatformDisplayName("blubrry")} stackId="a" fillOpacity={0.7} fill={PLATFORMS.BLUBRRY.color} />}
                            {isBar && <Bar dataKey="spotify" name={getPlatformDisplayName("spotify")} stackId="a" fillOpacity={0.7} fill={PLATFORMS.SPOTIFY.color} />}
                            {isBar && <Bar dataKey="apple" name={getPlatformDisplayName("apple")} stackId="a" fillOpacity={0.7} fill={PLATFORMS.APPLE.color} />}
                            {isLine && <Line type="monotone" dataKey="total" fill={theme.palette.primary.light} stroke={theme.palette.primary.light} strokeWidth={3} />}

                        </ComposedChart>
                    </ResponsiveContainer>}
                </Grid>
            </Grid >

        </>
    )
}
